import L from "leaflet";
import type { ViewApi } from "./api";
import type { BoundsTuple, LatLngTuple } from "./options";
import type { MapStore } from "./store";
import { format } from "../i18n/labels";
import type { Labels } from "../i18n/labels";

export const fitPadding: L.PointTuple = [48, 48];

export const fitMaxZoom = 17;

export const locateZoom = 16;

export const locatePane = "fxmap-locate";

export const locateColor = "#0E6B7A";

export type ViewControllerOptions = {
  map: L.Map;
  container: HTMLElement;
  store: MapStore;
  labels: Labels;
  animate: boolean;
  contentBounds(): L.LatLngBounds | null;
  fallbackBounds: BoundsTuple | null;
  homeView: { center: LatLngTuple; zoom: number };
};

export type ViewController = ViewApi & { dispose(): void; locating(): boolean };

export function toBounds(bounds: BoundsTuple | L.LatLngBounds): L.LatLngBounds {
  return bounds instanceof L.LatLngBounds ? bounds : L.latLngBounds(bounds[0], bounds[1]);
}

export function boundsTuple(bounds: L.LatLngBounds): BoundsTuple {
  return [
    [bounds.getSouth(), bounds.getWest()],
    [bounds.getNorth(), bounds.getEast()]
  ];
}

export function createViewController(options: ViewControllerOptions): ViewController {
  const { map, store, labels, animate } = options;
  const pane = map.getPane(locatePane) ?? map.createPane(locatePane);

  pane.style.zIndex = "850";

  const group = L.layerGroup();
  let circle: L.Circle | null = null;
  let dot: L.CircleMarker | null = null;
  let watching = false;
  let following = false;

  const stopLocate = () => {
    if (!watching) return;

    watching = false;
    following = false;
    map.stopLocate();
    group.clearLayers();

    if (map.hasLayer(group)) map.removeLayer(group);

    circle = null;
    dot = null;
    options.container.removeAttribute("data-fxmap-locating");
  };

  const onFound = (event: L.LocationEvent) => {
    if (!watching) return;
    if (!map.hasLayer(group)) group.addTo(map);

    const accuracyText = format(labels.locateAccuracy, { meters: Math.round(event.accuracy) });

    if (circle === null) {
      circle = L.circle(event.latlng, { radius: event.accuracy, pane: locatePane, color: locateColor, weight: 1, fillColor: locateColor, fillOpacity: 0.12, interactive: false, className: "fxmap-locate-accuracy" }).addTo(group);
      dot = L.circleMarker(event.latlng, { radius: 7, pane: locatePane, color: "#ffffff", weight: 3, fillColor: locateColor, fillOpacity: 1, className: "fxmap-locate-dot" }).addTo(group);
      dot.bindTooltip(accuracyText, { direction: "top" });
    } else {
      circle.setLatLng(event.latlng);
      circle.setRadius(event.accuracy);
      dot?.setLatLng(event.latlng);
      dot?.setTooltipContent(accuracyText);
    }

    if (!following) return;

    const zoom = Math.max(map.getZoom(), locateZoom);

    if (animate) map.flyTo(event.latlng, zoom, { duration: 0.6 });
    else map.setView(event.latlng, zoom, { animate: false });
  };

  const onError = () => {
    stopLocate();
    store.getState().pushToast({ severity: "warning", text: labels.locateFailed });
  };

  const onDragStart = () => {
    following = false;
  };

  map.on("locationfound", onFound);
  map.on("locationerror", onError);
  map.on("dragstart", onDragStart);

  const fitContent = () => {
    const bounds = options.contentBounds() ?? (options.fallbackBounds === null ? null : toBounds(options.fallbackBounds));

    if (bounds !== null && bounds.isValid()) {
      map.fitBounds(bounds, { padding: fitPadding, animate, maxZoom: fitMaxZoom });
      return;
    }

    map.setView(options.homeView.center, options.homeView.zoom, { animate });
  };

  const toggleFullscreen = () => {
    const target = options.container;

    if (typeof document === "undefined") return;

    if (document.fullscreenElement === target) {
      void document.exitFullscreen?.();
      return;
    }

    if (typeof target.requestFullscreen === "function") {
      void target.requestFullscreen().catch(() => {
        target.classList.toggle("fxmap-fullscreen");
        map.invalidateSize({ animate: false });
      });

      return;
    }

    target.classList.toggle("fxmap-fullscreen");
    map.invalidateSize({ animate: false });
  };

  return {
    fitContent,
    fitBounds: (bounds, fitOptions) => map.fitBounds(toBounds(bounds), { padding: fitPadding, animate, ...(fitOptions ?? {}) }),
    setView: (center: LatLngTuple, zoom?: number) => {
      map.setView(center, zoom ?? map.getZoom(), { animate });
    },
    panTo: (center: LatLngTuple) => {
      map.panTo(center, { animate });
    },
    zoomIn: () => map.zoomIn(1, { animate }),
    zoomOut: () => map.zoomOut(1, { animate }),
    locate: () => {
      if (watching) {
        stopLocate();
        return;
      }

      watching = true;
      following = true;
      options.container.setAttribute("data-fxmap-locating", "true");
      map.locate({ watch: true, enableHighAccuracy: true, setView: false });
    },
    stopLocate,
    locating: () => watching,
    getCenter: () => {
      const center = map.getCenter();

      return [center.lat, center.lng];
    },
    getZoom: () => map.getZoom(),
    getBounds: () => boundsTuple(map.getBounds()),
    invalidateSize: () => map.invalidateSize({ animate: false }),
    toggleFullscreen,
    dispose: () => {
      stopLocate();
      map.off("locationfound", onFound);
      map.off("locationerror", onError);
      map.off("dragstart", onDragStart);
    }
  };
}
